import React, { useState } from 'react';
import {
  Box,
  Typography,
  List,
  ListItem,
  ListItemButton,
  Stack,
  Checkbox,
  Container,
  TextField,
} from '@mui/material';
import { Button } from '../../../../Components/MUIs';
import FilterBar from '../../../../Components/Ui/FilterBar';
import { useDispatch } from 'react-redux';
import { useMemo } from 'react';
import { setDepCd } from '../../../../Reducer/SearchReducer';
import SearchBar from '../../../../Components/Ui/SearchBar';

export default function M_EIC_NEW(props) {
  const dispatch = useDispatch();
  const [searchKeyword, setSearchKeyword] = useState('');
  const filterState = props.filterState ?? {};

  const selectedEmployees = useMemo(
    () => props.selectedEmployees ?? [],
    [props.selectedEmployees]
  );
  const selectedEmployeeIds = useMemo(
    () => new Set(selectedEmployees.map((e) => e.id)),
    [selectedEmployees]
  );

  const depOptions = (props.depList ?? []).map((d) => ({
    value: d.value ?? '',
    label: d.name ?? d.label ?? '',
  }));
  const filterItems = useMemo(
    () => [{ key: 'dep', label: '부서', value: filterState.depName ?? '전체' }],
    [filterState.depName]
  );

  const filterOptionsByKey = useMemo(
    () => ({
      dep: depOptions,
    }),
    [depOptions]
  );

  const HandleSearchChange = (e) => {
    setSearchKeyword(e.target.value);
  };

  const filteredEmployeeList = useMemo(() => {
    const keyword = searchKeyword.trim().toLowerCase();
    const selectedDepCd = props.filterState?.depCd ?? '';

    return (props.employeeList ?? []).filter((employee) => {
      const employeeDepCd = employee?.depCd ?? '';
      const isDepMatched = !selectedDepCd || employeeDepCd === selectedDepCd;
      if (!isDepMatched) return false;

      if (!keyword) return true;

      const employeeName = (employee?.name ?? '').toLowerCase();
      const employeeId = String(employee?.id ?? '').toLowerCase();
      return employeeName.includes(keyword) || employeeId.includes(keyword);
    });
  }, [props.employeeList, props.filterState?.depCd, searchKeyword]);

  // 특정 부서가 선택된 경우에만 다중 선택 모드 활성화
  const isDepSelected = !!(filterState.depCd ?? '');

  const allSelected =
    isDepSelected &&
    filteredEmployeeList.length > 0 &&
    filteredEmployeeList.every((e) => selectedEmployeeIds.has(e.id));
  const someSelected =
    isDepSelected &&
    !allSelected &&
    filteredEmployeeList.some((e) => selectedEmployeeIds.has(e.id));

  const HandleSelectAllToggle = () => {
    if (allSelected) {
      props.onDeselectAll();
    } else {
      props.onSelectAll(filteredEmployeeList);
    }
  };

  const HandleFilterSelect = (key, option) => {
    if (key === 'dep') {
      dispatch(setDepCd(option.value));
    }
  };

  const selectedCountLabel = useMemo(() => {
    const count = selectedEmployees.length;
    if (count === 0) return null;
    if (count === 1) return `${selectedEmployees[0].name}님`;
    return `${selectedEmployees[0].name} 외 ${count - 1}명`;
  }, [selectedEmployees]);

  return (
    <div className="flex h-full min-h-0 flex-col">
      <SearchBar
        value={searchKeyword}
        onChange={HandleSearchChange}
        placeholder="이름 또는 ID 검색"
      />

      <FilterBar
        items={filterItems}
        optionsByKey={filterOptionsByKey}
        onSelect={HandleFilterSelect}
      />

      <Container
        sx={{
          py: 2,
          flex: 1,
          minHeight: 0,
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <Box sx={{ display: 'flex', gap: 2, flex: 1, minHeight: 0 }}>
          {/* 왼쪽: 근로자 목록 */}
          <Box sx={{ width: '40%', borderRight: '1px solid #e0e0e0', pr: 1.5 }}>
            <Box sx={{ mb: 1.5, px: 0.5 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                {isDepSelected && (
                  <Checkbox
                    checked={allSelected}
                    indeterminate={someSelected}
                    onChange={HandleSelectAllToggle}
                    disabled={filteredEmployeeList.length === 0}
                    size="small"
                    sx={{ p: 0 }}
                  />
                )}
                <Typography
                  variant="subtitle1"
                  sx={{
                    fontWeight: 'bold',
                    fontSize: '15px',
                    whiteSpace: 'nowrap', // 절대 줄바꿈 안 되게 차단!
                  }}
                >
                  근로자 목록
                </Typography>
              </Box>
              <Box
                sx={{ height: '18px', mt: 0.5, pl: isDepSelected ? '28px' : 0 }}
              >
                <Typography
                  variant="caption"
                  sx={{
                    color: '#1976d2',
                    fontWeight: 'bold',
                    display: 'block',
                  }}
                >
                  {selectedEmployees.length}명 선택됨
                </Typography>
              </Box>
            </Box>

            <Box
              sx={{
                height: 'calc(100% - 44px)',
                overflow: 'auto',
                '&::-webkit-scrollbar': {
                  display: 'none',
                },
              }}
            >
              {filteredEmployeeList.length === 0 ? (
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mt: 2, px: 0.5 }}
                >
                  {searchKeyword.trim()
                    ? '검색 결과가 없습니다.'
                    : '등록된 근로자가 없습니다.'}
                </Typography>
              ) : (
                <List
                  sx={{
                    p: 0,
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '6px',
                  }}
                >
                  {' '}
                  {/* 아이템 간 간격 지정 */}
                  {filteredEmployeeList.map((employee) => {
                    const isSelected = selectedEmployeeIds.has(employee.id);
                    return (
                      <ListItem key={employee.id} disablePadding>
                        <ListItemButton
                          onClick={() =>
                            isDepSelected
                              ? props.onEmployeeToggle(employee)
                              : props.onEmployeeSingleSelect(employee)
                          }
                          sx={{
                            p: '10px 12px', // 내부 패딩 최적화
                            borderRadius: '8px',
                            backgroundColor: isSelected ? '#e3f2fd' : '#f8f9fa', // 비선택시에도 연한 배경 적용 가능 (선택사항)
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1,
                            transition: 'all 0.2s',
                            '&:hover': {
                              backgroundColor: isSelected
                                ? '#bbdefb'
                                : '#f1f3f5',
                            },
                            '&.Mui-selected': {
                              backgroundColor: '#e3f2fd',
                              '&:hover': { backgroundColor: '#bbdefb' },
                            },
                          }}
                          selected={!isDepSelected && isSelected}
                        >
                          {/* 다중 선택 모드일 때만 체크박스 노출 */}
                          {isDepSelected && (
                            <Checkbox
                              checked={isSelected}
                              size="small"
                              onClick={(e) => e.stopPropagation()}
                              onChange={() => props.onEmployeeToggle(employee)}
                              sx={{ p: 0 }} // 체크박스 자체 패딩 제거해서 여백 확보
                            />
                          )}

                          {/* 텍스트 레이아웃 커스텀 개조 (줄바꿈 방지) */}
                          <Box
                            sx={{
                              flex: 1,
                              minWidth: 0,
                              display: 'flex',
                              flexDirection: 'column',
                              gap: '2px',
                            }}
                          >
                            <Typography
                              sx={{
                                fontSize: '14px',
                                fontWeight: isSelected ? '700' : '500',
                                color: isSelected ? '#1565c0' : '#212529',
                                whiteSpace: 'nowrap',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                              }}
                            >
                              {employee.name}
                            </Typography>
                            <Typography
                              sx={{
                                fontSize: '11px',
                                color: '#6c757d',
                                whiteSpace: 'nowrap',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                              }}
                            >
                              {[employee.rank, employee.depNm]
                                .filter(Boolean)
                                .join(' | ')}
                            </Typography>
                          </Box>
                        </ListItemButton>
                      </ListItem>
                    );
                  })}
                </List>
              )}
            </Box>
          </Box>

          {/* 오른쪽: 보호구 등록 영역 */}
          <Box
            sx={{ flex: 1, display: 'flex', flexDirection: 'column', pl: 0.5 }}
          >
            {selectedEmployees.length > 0 ? (
              <>
                {/* 상단 헤더 영역 */}
                <Box sx={{ mb: 2 }}>
                  <Typography
                    variant="subtitle1"
                    sx={{
                      fontWeight: 'bold',
                      fontSize: '16px',
                      lineHeight: 1.3,
                      wordBreak: 'keep-all', // 단어 단위 줄바꿈으로 가독성 향상
                      color: '#212529',
                    }}
                  >
                    {selectedCountLabel}에게 보호구 지급
                  </Typography>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{
                      display: 'block',
                      mt: 0.5,
                      fontSize: '11px',
                      lineHeight: 1.3,
                      wordBreak: 'keep-all',
                    }}
                  >
                    지급하실 보호구를 선택하세요
                    {selectedEmployees.length > 1 &&
                      ` (선택한 ${selectedEmployees.length}명 모두 동일하게 지급됩니다)`}
                  </Typography>
                </Box>

                {/* 보호구 리스트 영역 */}
                <Box
                  sx={{
                    flex: 1,
                    overflow: 'auto',
                    mb: 2,
                    pr: 0.5, // 스크롤 시 카드가 잘리지 않도록 미세 여백
                    '&::-webkit-scrollbar': {
                      display: 'none',
                    },
                  }}
                >
                  {props.equipList.length === 0 ? (
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ mt: 2, textAlign: 'center' }}
                    >
                      등록된 보호구가 없습니다.
                    </Typography>
                  ) : (
                    <Stack spacing={1.5}>
                      {props.equipList.map((equip) => {
                        const equipId = equip.erCd;
                        const isSelected =
                          equipId && props.selectedEquips.includes(equipId);

                        return (
                          <Box
                            key={equipId}
                            sx={{
                              border: isSelected
                                ? '1.5px solid #1976d2'
                                : '1px solid #e0e0e0',
                              borderRadius: '12px', // 조금 더 부드러운 라운딩
                              backgroundColor: '#fff',
                              boxShadow: isSelected
                                ? '0 4px 12px rgba(25, 118, 210, 0.08)'
                                : '0 2px 4px rgba(0,0,0,0.02)',
                              transition: 'all 0.2s ease',
                              overflow: 'hidden',
                            }}
                          >
                            <Box
                              sx={{
                                p: 1.5,
                                display: 'flex',
                                flexDirection: 'column',
                                gap: 1.5,
                              }}
                            >
                              {/* 상단: 체크박스 + 이름 + 재고 */}
                              <Box
                                sx={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: 1,
                                }}
                              >
                                <Checkbox
                                  checked={isSelected}
                                  onChange={() => props.onEquipToggle(equipId)}
                                  size="small"
                                  sx={{ p: 0 }} // 패딩 제거로 가로 폭 확보
                                />
                                <Box
                                  sx={{
                                    flex: 1,
                                    minWidth: 0,
                                    display: 'flex',
                                    alignItems: 'baseline',
                                    gap: 0.5,
                                  }}
                                >
                                  <Typography
                                    variant="body2"
                                    sx={{
                                      fontWeight: isSelected ? '700' : '500',
                                      fontSize: '14px',
                                      color: isSelected ? '#1976d2' : '#212529',
                                      whiteSpace: 'nowrap',
                                      overflow: 'hidden',
                                      textOverflow: 'ellipsis',
                                    }}
                                  >
                                    {equip.equipNm}
                                  </Typography>
                                  <Typography
                                    variant="caption"
                                    sx={{
                                      fontSize: '11px',
                                      color: '#6c757d',
                                      whiteSpace: 'nowrap',
                                    }}
                                  >
                                    (재고: {equip.stockQty})
                                  </Typography>
                                </Box>
                              </Box>

                              {/* 하단: 선택되었을 때만 노출되는 입력 폼 */}
                              {isSelected && (
                                <Box
                                  sx={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    gap: 1,
                                    pt: 1.5,
                                    borderTop: '1px dashed #e0e0e0', // 구분선 추가로 상하 분리감 부여
                                  }}
                                >
                                  <TextField
                                    label="수량"
                                    type="number"
                                    size="small"
                                    fullWidth
                                    value={
                                      props.equipInputs[equipId]?.qty ?? ''
                                    }
                                    onFocus={() => {
                                      const currentQty =
                                        props.equipInputs[equipId]?.qty;
                                      if (currentQty === 1)
                                        props.onInputChange(equipId, 'qty', '');
                                    }}
                                    onChange={(e) =>
                                      props.onInputChange(
                                        equipId,
                                        'qty',
                                        e.target.value
                                      )
                                    }
                                    onBlur={() => {
                                      const currentQty =
                                        props.equipInputs[equipId]?.qty;
                                      if (currentQty === '')
                                        props.onInputChange(equipId, 'qty', 1);
                                    }}
                                    inputProps={{
                                      min: 0,
                                      style: { fontSize: '16px' },
                                    }}
                                    InputLabelProps={{
                                      style: { fontSize: '16px' },
                                    }}
                                  />
                                  <TextField
                                    label="비고"
                                    size="small"
                                    multiline
                                    rows={2}
                                    fullWidth
                                    value={
                                      props.equipInputs[equipId]?.remark || ''
                                    }
                                    onChange={(e) =>
                                      props.onInputChange(
                                        equipId,
                                        'remark',
                                        e.target.value
                                      )
                                    }
                                    inputProps={{ style: { fontSize: '16px' } }}
                                    InputLabelProps={{
                                      style: { fontSize: '16px' },
                                    }}
                                  />
                                </Box>
                              )}
                            </Box>
                          </Box>
                        );
                      })}
                    </Stack>
                  )}
                </Box>

                {/* 하단 저장 버튼 영역 */}
                <Box sx={{ pt: 1 }}>
                  <Button
                    name={
                      selectedEmployees.length > 1
                        ? `${selectedEmployees.length}명 일괄 저장`
                        : '저장'
                    }
                    sx={{
                      width: '100%',
                      height: 44, // 모바일 터치 영역 확장을 위해 약간 높임
                      borderRadius: '8px',
                      backgroundColor: '#57B84D',
                      color: '#fff',
                      fontWeight: 'bold',
                      fontSize: '14px',
                      boxShadow: '0 4px 6px rgba(87, 184, 77, 0.15)',
                      '&:hover': {
                        backgroundColor: '#4a9e42',
                      },
                      '&:disabled': {
                        backgroundColor: '#e0e0e0',
                        color: '#a1a1a1',
                      },
                    }}
                    onClick={props.onSave}
                    disabled={props.selectedEquips.length === 0}
                  />
                </Box>
              </>
            ) : (
              /* 근로자 미선택 시 안내 메시지 */
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  height: '100%',
                  px: 2,
                }}
              >
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ textAlign: 'center' }}
                >
                  왼쪽에서 근로자를 선택하세요
                </Typography>
              </Box>
            )}
          </Box>
        </Box>
      </Container>
    </div>
  );
}
